import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon } from 'lucide-react';
type TagType = 'Feature' | 'UI' | 'Platform' | 'Integration' | 'Security' | 'Performance' | 'Fix';
interface BulletItem {
  tag: TagType;
  area: string;
  text: string;
}
interface MonthSummary {
  month: string;
  key: string; // YYYY-MM for sorting
  items: BulletItem[];
}
const TAG_STYLES: Record<TagType, string> = {
  Feature: 'bg-violet-electric/15 text-violet-electric border border-violet-electric/25',
  UI: 'bg-blue-500/15 text-blue-400 border border-blue-500/25',
  Platform: 'bg-teal-500/15 text-teal-400 border border-teal-500/25',
  Integration: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25',
  Security: 'bg-amber-500/15 text-amber-400 border border-amber-500/25',
  Performance: 'bg-orange-500/15 text-orange-400 border border-orange-500/25',
  Fix: 'bg-red-500/15 text-red-400 border border-red-500/25'
};
const TAG_ACTIVE_STYLES: Record<TagType, string> = {
  Feature: 'bg-violet-electric/20 text-violet-electric border border-violet-electric/40',
  UI: 'bg-blue-500/20 text-blue-400 border border-blue-500/40',
  Platform: 'bg-teal-500/20 text-teal-400 border border-teal-500/40',
  Integration: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40',
  Security: 'bg-amber-500/20 text-amber-400 border border-amber-500/40',
  Performance: 'bg-orange-500/20 text-orange-400 border border-orange-500/40',
  Fix: 'bg-red-500/20 text-red-400 border border-red-500/40'
};
const ALL_TAGS: TagType[] = ['Feature', 'UI', 'Platform', 'Integration', 'Security', 'Performance', 'Fix'];
const MONTHLY_SUMMARIES: MonthSummary[] = [{
  month: 'May 2026',
  key: '2026-05',
  items: [{
    tag: 'Feature',
    area: 'Documents',
    text: 'In-app Knowledge Base launched — with foldable sidebars, generated table of contents, and a default "Getting Started" guide'
  }, {
    tag: 'Feature',
    area: 'Documents',
    text: 'Embeddable interactive demo components across platform guides, plus six static demos illustrating common patterns'
  }, {
    tag: 'Feature',
    area: 'Documents',
    text: 'Published guides covering builds, filters, CSV import/export, and platform basics'
  }, {
    tag: 'UI',
    area: 'Workflows',
    text: 'Major Approvals UI refresh — redesigned step layout and reviewer experience'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Step-level approve/reject workflows now fire with the run step user and item injected into workflow context'
  }, {
    tag: 'Platform',
    area: 'Workflows',
    text: 'Centralized approvals-with-inputs logic so all approval types share one path; approval input schemas now optional'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Workflow run queueing for ordered, reliable execution'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Workflows can now be archived and restored instead of hard-deleted; query actions support full triggering-item payloads'
  }, {
    tag: 'Fix',
    area: 'Workflows',
    text: 'Module workflow validation hardened against false negatives; context multiplicity fixes for parallel branches'
  }, {
    tag: 'Performance',
    area: 'Label Printing',
    text: 'Async label generation with WebSocket progress updates; multi-label generation now chunks large batches for reliability'
  }, {
    tag: 'UI',
    area: 'Admin',
    text: 'Admin pagination, filter, and search overhaul; list view styling refresh with inline inputs and conditional filter inputs'
  }, {
    tag: 'Fix',
    area: 'Admin',
    text: 'New fields no longer auto-add themselves to existing list layouts'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'New single-number, percentage, donut, and pie card variants backed by pivots'
  }, {
    tag: 'Fix',
    area: 'Pivot Tables',
    text: 'Pivot total row preview fix; joined-app subquery filter fix; cluster map updated for new pivot rendering'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Asset Management module added'
  }, {
    tag: 'Feature',
    area: 'Reports',
    text: 'Aggregate report export now supported in view mode'
  }, {
    tag: 'Fix',
    area: 'Admin',
    text: 'CORS + cookie fix for hyphen-prefix subdomain deployments; WebSocket reconnection fixes; Lambda configs added for new outbound integration tier'
  }]
}, {
  month: 'April 2026',
  key: '2026-04',
  items: [{
    tag: 'Platform',
    area: 'Admin',
    text: 'Global framework upgrade (Slice 1 of a multi-slice rollout) merged into main'
  }, {
    tag: 'Feature',
    area: 'AI',
    text: 'Thruline AI v1 launched (initial slice) — deprecates the legacy Bedrock-based KB AI prompt system'
  }, {
    tag: 'Platform',
    area: 'Admin',
    text: 'Multi-pod support for dedicated clusters; multi-superuser support with performance improvements'
  }, {
    tag: 'Security',
    area: 'Admin',
    text: 'LMS role gating — enforcement for lms-admin and lms-supervisor roles'
  }, {
    tag: 'Feature',
    area: 'UI/UX',
    text: '"Favorites" added to the left navigation menu; menu items can now be created as direct links'
  }, {
    tag: 'Fix',
    area: 'Admin',
    text: 'SSO login fixes (org change + change detection); iOS-specific troubleshooting and stability fixes'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Step-level approve and reject workflows; approval run-conditions workflows; cleaner abort when no eligible users are found'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Bulk / async "Update App" actions across large datasets; pre-create set-values for new-item workflows'
  }, {
    tag: 'Feature',
    area: 'Forms',
    text: 'Form conditions can now reset item values when conditions flip; reference clearing respects form-condition checks'
  }, {
    tag: 'Fix',
    area: 'Forms',
    text: '"Unique combinations" now allow blank/empty fields without false collisions; Project Stakeholders field added'
  }, {
    tag: 'UI',
    area: 'Pivot Tables',
    text: 'Pivot table view refresh; pivot-backed cards for dashboards; pivot passthrough filters end-to-end (API + UI)'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Pivot group displays, dynamic IN filtering, reverse-lookup base filtering, and dynamic pivot-app values in subqueries'
  }, {
    tag: 'Feature',
    area: 'Reports',
    text: 'Calculated report inputs and report input reference display fields'
  }, {
    tag: 'Feature',
    area: 'Label Printing',
    text: 'QR codes in HTML templates (plus removal of stale QR attachments)'
  }, {
    tag: 'Feature',
    area: 'UI/UX',
    text: '"Copy selected list rows to clipboard as CSV"; related list counts now update live on item CRUD'
  }, {
    tag: 'Fix',
    area: 'UI/UX',
    text: 'Related lists no longer restricted by base-app permissions; all-settled list-actions so one failure no longer aborts the batch'
  }, {
    tag: 'Fix',
    area: 'Admin',
    text: 'Intermittent CORS fix; mentions fixes post-upgrade; multi-tab workflow redirect fixes'
  }]
}, {
  month: 'March 2026',
  key: '2026-03',
  items: [{
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'New Manufacturing Order Planning UI with production-planning Gantt chart and routing view'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'MO autofill from BoM routing; procedure-run state changes on MO routing steps; routing-step form conditions'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Bundles can now include sellable products; MO Procedures module updates with inventory visibility'
  }, {
    tag: 'Feature',
    area: 'Sales & CRM',
    text: 'New Quote → Sales Order conversion flow with quote context generation workflow'
  }, {
    tag: 'Feature',
    area: 'Sales & CRM',
    text: 'Currency support for HTML templates and quotes, with dynamic currency type configuration'
  }, {
    tag: 'Feature',
    area: 'Admin',
    text: 'S3 org data backup with unique-per-org pathing; backup-org modal, settings, and region selection'
  }, {
    tag: 'Performance',
    area: 'Admin',
    text: 'Org-copy converted to streaming for large orgs'
  }, {
    tag: 'Feature',
    area: 'Label Printing',
    text: 'Export Labels modal component, list-action labels, PNG label sheet rows, and label-friendly file names'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Dashboard and card-level inputs, including donut/pie cards; pivot inputs on dashboards'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Pivot table inline field columns, sticky card headers, display fields in export, and aggregation label sizing'
  }, {
    tag: 'Performance',
    area: 'Reports',
    text: 'Report caching with current-user hash; report-input reference fixes and fallback to empty'
  }, {
    tag: 'Feature',
    area: 'Forms',
    text: 'Field-type converter for compatible field types; UoM-compatible service for cross-unit conversions'
  }, {
    tag: 'Feature',
    area: 'Forms',
    text: '"Skip overwrite on update all" for safer module updates; "Disable multi new row until loaded" to prevent half-saved rows'
  }, {
    tag: 'Feature',
    area: 'UI/UX',
    text: 'Gantt chart with inputs, user settings, secondary apps support, paging options, and dependency field updates'
  }, {
    tag: 'Feature',
    area: 'UI/UX',
    text: 'Task scheduler can now create across multiple apps; task schedules "flip thru" navigation'
  }, {
    tag: 'Fix',
    area: 'Workflows',
    text: 'Workflow abort on CRUD actions; workflow context fixes on view operations; module workflow viewer added'
  }, {
    tag: 'Platform',
    area: 'Admin',
    text: 'CI / code pipeline updates, cluster deploy Lambda changes for external clusters, and PDF customization and export style updates'
  }]
}, {
  month: 'February 2026',
  key: '2026-02',
  items: [{
    tag: 'Feature',
    area: 'Sales & CRM',
    text: 'Custom quotes UI — fully branded quote documents with configurable layouts'
  }, {
    tag: 'Feature',
    area: 'Sales & CRM',
    text: 'CRM module overhauled with new account, deal, and contact configurations'
  }, {
    tag: 'Feature',
    area: 'Inventory',
    text: 'Duplicate serial number detection and enforcement across all inventory transactions'
  }, {
    tag: 'Platform',
    area: 'Admin',
    text: 'Concurrent record update conflict detection — prevents data overwrites when multiple users edit simultaneously'
  }, {
    tag: 'Feature',
    area: 'Purchasing',
    text: 'Purchasing test suites and purchase receipt validation'
  }, {
    tag: 'Platform',
    area: 'Workflows',
    text: 'For-each reduce variable support and scheduled workflow next-run improvements'
  }, {
    tag: 'UI',
    area: 'Forms',
    text: 'In-place form condition references for faster workflow configuration'
  }]
}, {
  month: 'January 2026',
  key: '2026-01',
  items: [{
    tag: 'Feature',
    area: 'AI',
    text: 'AI knowledge base system — combine multiple knowledge sources for smarter prompts'
  }, {
    tag: 'Feature',
    area: 'Compliance',
    text: 'Vendor watch list module — automated Trade.gov compliance screening for all vendors'
  }, {
    tag: 'Feature',
    area: 'Inventory',
    text: 'Serial number uniqueness enforcement across all inventory and manufacturing records'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Vertical groupings, group-by date ranges, and aggregate values as links'
  }, {
    tag: 'Security',
    area: 'Admin',
    text: 'Multiple users per single auth account — improved multi-user organization management'
  }, {
    tag: 'Feature',
    area: 'Forms',
    text: 'Role-based form conditions — show or hide fields based on user role'
  }, {
    tag: 'Feature',
    area: 'Purchasing',
    text: 'BEM Sales Order workflow with API data schema and webhook integration'
  }]
}, {
  month: 'December 2025',
  key: '2025-12',
  items: [{
    tag: 'Feature',
    area: 'AI',
    text: 'AI system launched — built on AWS Bedrock with OpenSearch vector store; first AI prompt action live in workflows'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'As-built module tree for tracking full assembly genealogy of manufactured products'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'BOM operation cost tracking — capture labor and overhead costs at the operation level'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Workflow tree conditions for complex branching logic; list actions now support user input prompts'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Pivot table PDF export with no-wrap formatting'
  }, {
    tag: 'Security',
    area: 'Admin',
    text: 'Super admin mode and all-app permissions configuration for enterprise deployments'
  }, {
    tag: 'Platform',
    area: 'Import/Export',
    text: 'Run workflows on data imports with enhanced execution history'
  }]
}, {
  month: 'November 2025',
  key: '2025-11',
  items: [{
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'MO Demand Planner module — visualize and plan manufacturing demand from sales orders'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Standard stock valuation report and PLM BOM updates'
  }, {
    tag: 'Feature',
    area: 'Inventory',
    text: 'Inventory site transfers with consumed and returned MO reason tracking'
  }, {
    tag: 'Feature',
    area: 'Sales & CRM',
    text: 'Quote line bundles — group products into configurable bundle configurations'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'FIFO pivot table for inventory costing and demand planner module'
  }, {
    tag: 'Feature',
    area: 'Documents',
    text: 'PDF download for shared Blueprint pages'
  }, {
    tag: 'Feature',
    area: 'Procedures',
    text: 'Procedure form conditions and superseded status tracking'
  }]
}, {
  month: 'October 2025',
  key: '2025-10',
  items: [{
    tag: 'UI',
    area: 'Dashboard',
    text: 'Dashboard fully refactored with improved layout and navigation'
  }, {
    tag: 'Feature',
    area: 'UI/UX',
    text: 'Autosave for forms — changes saved automatically as you work'
  }, {
    tag: 'Feature',
    area: 'UI/UX',
    text: 'Flip-through modal for browsing records without leaving context'
  }, {
    tag: 'Feature',
    area: 'Notifications',
    text: 'Notifications system overhauled — new UI, subscriber-only option, async delivery'
  }, {
    tag: 'Feature',
    area: 'Reports',
    text: 'Excel export for aggregate reports and aggregate formula tree support'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Sticky headers, copy-link sharing, and dashboard export for pivot tables'
  }, {
    tag: 'Feature',
    area: 'Label Printing',
    text: 'HTML template support for fully custom label layouts'
  }]
}, {
  month: 'September 2025',
  key: '2025-09',
  items: [{
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Manufacturing procedures module — attach step-by-step instructions to production orders'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Scrap line tracking, re-open MO capability, and manufacturing line statuses'
  }, {
    tag: 'Feature',
    area: 'Sales & CRM',
    text: 'Sellable product bundles — configure and sell product kits directly from sales orders'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'PO-to-MO pivot view for manufacturing demand planning'
  }, {
    tag: 'Integration',
    area: 'Integrations',
    text: 'WooCommerce webhook integration and Slack workflow action reliability improvements'
  }, {
    tag: 'Feature',
    area: 'Documents',
    text: 'Mentions support in Blueprint requirements for team collaboration'
  }, {
    tag: 'Feature',
    area: 'Import/Export',
    text: 'Async CSV imports for large files, export CSV with script support'
  }]
}, {
  month: 'August 2025',
  key: '2025-08',
  items: [{
    tag: 'Feature',
    area: 'PLM',
    text: 'PLM (Product Lifecycle Management) module launched — manage product revisions and BOM changes'
  }, {
    tag: 'Feature',
    area: 'Documents',
    text: 'Blueprint document system — editable documents with export to HTML, PDF, and more'
  }, {
    tag: 'Feature',
    area: 'AI',
    text: 'AI prompt functionality launched with backend and frontend integration'
  }, {
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Unit of Measure compatibility across parts, BOM lines, and vendor parts; subassembly creation on MOs'
  }, {
    tag: 'Integration',
    area: 'Integrations',
    text: 'Zapier inbox automation — PDFs auto-processed from email; Onshape configuration data fetch'
  }, {
    tag: 'UI',
    area: 'UI/UX',
    text: 'Left menu refactored with new structure and universal icons'
  }]
}, {
  month: 'July 2025',
  key: '2025-07',
  items: [{
    tag: 'Feature',
    area: 'Documents',
    text: 'Blueprint by Thruline launched — new document management system with PDF and export support'
  }, {
    tag: 'Feature',
    area: 'Notifications',
    text: 'Inline approval buttons in emails — approve or reject directly from your inbox'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Workflow return values, infinite loop detection, and attachment querying'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Multi-select rows, upstream script items, and HTML content in pivot exports'
  }, {
    tag: 'Feature',
    area: 'Reports',
    text: 'Aggregate report sticky headers and view-only report links'
  }, {
    tag: 'Integration',
    area: 'Integrations',
    text: 'Photon document scanning integration'
  }]
}, {
  month: 'June 2025',
  key: '2025-06',
  items: [{
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Asset management module for tracking equipment and production assets'
  }, {
    tag: 'Security',
    area: 'Admin',
    text: 'Entra SAML SSO setup for enterprise single sign-on'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Workflow abort with custom user-facing messages; copy/paste workflow actions'
  }, {
    tag: 'Feature',
    area: 'Accounting',
    text: 'Transaction matching for bills, chart of accounts updates, and Ramp transaction fields'
  }, {
    tag: 'Feature',
    area: 'Reports',
    text: 'Aggregate report section totals and performance improvements'
  }]
}, {
  month: 'May 2025',
  key: '2025-05',
  items: [{
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Core manufacturing module launched — manufacturing orders, BOM, and costing workflows'
  }, {
    tag: 'Feature',
    area: 'Accounting',
    text: 'Billing-accounting module with invoice tax, payment support, and unmatched transaction filtering'
  }, {
    tag: 'Feature',
    area: 'Label Printing',
    text: 'QR code support in label layouts'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'On-reject workflow support for approval automation'
  }, {
    tag: 'Feature',
    area: 'Import/Export',
    text: 'Bulk import/export of items'
  }]
}, {
  month: 'April 2025',
  key: '2025-04',
  items: [{
    tag: 'Integration',
    area: 'Integrations',
    text: 'Onshape thumbnail support and Ramp expense management module'
  }, {
    tag: 'Feature',
    area: 'Label Printing',
    text: 'Label layout item references for dynamic label content'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Set variable action for storing and reusing values across workflow steps'
  }, {
    tag: 'UI',
    area: 'Forms',
    text: 'Modal header fields from layout and form studio unassigned field management'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Item-level column reordering, export ordering, and multi lookup-by group-by'
  }]
}, {
  month: 'March 2025',
  key: '2025-03',
  items: [{
    tag: 'Feature',
    area: 'Manufacturing',
    text: 'Quality Management module launched — inspection workflows, NCRs, and corrective actions'
  }, {
    tag: 'Feature',
    area: 'Accounting',
    text: 'Bank reconciliation screen with Plaid sync and transaction matching'
  }, {
    tag: 'Feature',
    area: 'Inventory',
    text: 'Inventory count functionality with transfer date tracking'
  }, {
    tag: 'Platform',
    area: 'PDF Generation',
    text: 'Puppeteer HTML-to-PDF engine for higher-quality document generation'
  }, {
    tag: 'UI',
    area: 'Forms',
    text: 'New form layout structure with editor, conditions logic, and read-only mode support'
  }]
}, {
  month: 'February 2025',
  key: '2025-02',
  items: [{
    tag: 'Feature',
    area: 'Sales & CRM',
    text: 'Quotes and price lists added to the sales module'
  }, {
    tag: 'Integration',
    area: 'Accounting',
    text: 'Plaid banking integration revamped with improved sync and reconciliation'
  }, {
    tag: 'Security',
    area: 'Admin',
    text: 'GCC High setup for government cloud compliance'
  }, {
    tag: 'Performance',
    area: 'Workflows',
    text: 'Workflow snapshot optimization, async for-each activation, and speed improvements'
  }]
}, {
  month: 'January 2025',
  key: '2025-01',
  items: [{
    tag: 'Integration',
    area: 'Integrations',
    text: 'WooCommerce integration module launched'
  }, {
    tag: 'Feature',
    area: 'Pivot Tables',
    text: 'Pivot table foundation: calculated group-level items, scripted styling, multi-config support, and column reordering'
  }, {
    tag: 'Feature',
    area: 'Workflows',
    text: 'Workflow last-run tracking and cascade delete workflow support'
  }, {
    tag: 'Feature',
    area: 'Import/Export',
    text: 'Import and export on related lists'
  }]
}];
const TOTAL_MONTHS = MONTHLY_SUMMARIES.length;
const TOTAL_HIGHLIGHTS = MONTHLY_SUMMARIES.reduce((sum, m) => sum + m.items.length, 0);
// Derive sorted unique areas
const areaCounts: Record<string, number> = {};
MONTHLY_SUMMARIES.forEach(m => m.items.forEach(item => {
  areaCounts[item.area] = (areaCounts[item.area] || 0) + 1;
}));
const SORTED_AREAS = Object.entries(areaCounts).sort((a, b) => b[1] - a[1]).map(([area]) => area);
const TAG_DOT_COLORS: Record<TagType, string> = {
  Feature: 'text-violet-electric',
  UI: 'text-blue-400',
  Platform: 'text-teal-400',
  Integration: 'text-emerald-400',
  Security: 'text-amber-400',
  Performance: 'text-orange-400',
  Fix: 'text-red-400'
};
function TagBadge({
  tag
}: {
  tag: TagType;
}) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase flex-shrink-0 ${TAG_STYLES[tag]}`} data-id="element-830">
      {tag}
    </span>;
}
export function ChangelogPage() {
  const [activeTagTypes, setActiveTagTypes] = useState<Set<TagType>>(new Set(ALL_TAGS));
  const [activeAreas, setActiveAreas] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const filteredMonths = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return MONTHLY_SUMMARIES.map(month => ({
      ...month,
      items: month.items.filter(item => {
        if (!activeTagTypes.has(item.tag)) return false;
        if (activeAreas.size > 0 && !activeAreas.has(item.area)) return false;
        if (q && !item.text.toLowerCase().includes(q) && !item.area.toLowerCase().includes(q)) return false;
        return true;
      })
    })).filter(month => month.items.length > 0);
  }, [activeTagTypes, activeAreas, searchQuery]);
  const showingCount = useMemo(() => filteredMonths.reduce((sum, m) => sum + m.items.length, 0), [filteredMonths]);
  function toggleTag(tag: TagType) {
    setActiveTagTypes(prev => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }
  function toggleArea(area: string) {
    setActiveAreas(prev => {
      const next = new Set(prev);
      if (next.has(area)) {
        next.delete(area);
      } else {
        next.add(area);
      }
      return next;
    });
  }
  function clearAreas() {
    setActiveAreas(new Set());
  }
  return <div className="min-h-screen bg-navy text-white" data-id="element-831">
      {/* Background glow */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full pointer-events-none z-0" style={{
      background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.12) 0%, transparent 70%)'
    }} data-id="element-832" />

      {/* Hero Header */}
      <motion.div className="relative z-10 max-w-4xl mx-auto px-6 pt-32 pb-10" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-833">
        <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-834">
          Development
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-3" data-id="element-835">
          Changelog
        </h1>
        <p className="text-white/50 text-base leading-relaxed max-w-xl mb-8" data-id="element-836">
          A curated summary of what we've shipped — focused on the changes that
          matter most to you.
        </p>

        {/* Stats */}
        <div className="flex items-center gap-8" data-id="element-837">
          <div data-id="element-838">
            <div className="text-2xl font-bold text-white font-mono" data-id="element-839">
              {TOTAL_MONTHS}
            </div>
            <div className="text-[10px] text-white/30 uppercase tracking-widest mt-0.5" data-id="element-840">
              Total Months
            </div>
          </div>
          <div className="w-px h-8 bg-white/[0.08]" data-id="element-841" />
          <div data-id="element-842">
            <div className="text-2xl font-bold text-white font-mono" data-id="element-843">
              {TOTAL_HIGHLIGHTS}
            </div>
            <div className="text-[10px] text-white/30 uppercase tracking-widest mt-0.5" data-id="element-844">
              Total Highlights
            </div>
          </div>
          <div className="w-px h-8 bg-white/[0.08]" data-id="element-845" />
          <div data-id="element-846">
            <div className="text-2xl font-bold text-violet-electric font-mono" data-id="element-847">
              {showingCount}
            </div>
            <div className="text-[10px] text-white/30 uppercase tracking-widest mt-0.5" data-id="element-848">
              Showing
            </div>
          </div>
        </div>
      </motion.div>

      {/* Sticky Toolbar */}
      <div className="sticky top-16 z-40 bg-navy/90 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-849">
        <div className="max-w-4xl mx-auto px-6 py-3" data-id="element-850">
          {/* Row 1: Search + Tag type filters */}
          <div className="flex items-center gap-3 flex-wrap" data-id="element-851">
            <div className="relative flex-1 min-w-[180px]" data-id="element-852">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" data-id="element-853" />
              <input type="text" placeholder="Search changes…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-white/[0.04] border border-white/10 rounded-lg text-sm text-white placeholder-white/25 outline-none focus:border-violet-electric/40 transition-colors font-mono" data-id="element-854" />
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap" data-id="element-855">
              {ALL_TAGS.map(tag => <button key={tag} onClick={() => toggleTag(tag)} className={`px-2.5 py-1 rounded text-[10px] font-bold tracking-wide uppercase border transition-all ${activeTagTypes.has(tag) ? TAG_ACTIVE_STYLES[tag] : 'bg-transparent border-white/10 text-white/25'}`} data-id="element-856">
                  {tag}
                </button>)}
            </div>
          </div>

          {/* Row 2: Area pills */}
          <div className="flex items-center gap-1.5 mt-2.5 overflow-x-auto pb-0.5" data-id="element-857">
            <button onClick={clearAreas} className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-mono font-medium border transition-all ${activeAreas.size === 0 ? 'bg-violet-electric/15 border-violet-electric/30 text-violet-electric' : 'bg-white/[0.03] border-white/10 text-white/40 hover:text-white/60'}`} data-id="element-858">
              All
            </button>
            {SORTED_AREAS.map(area => <button key={area} onClick={() => toggleArea(area)} className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-mono font-medium border transition-all whitespace-nowrap ${activeAreas.has(area) ? 'bg-violet-electric/15 border-violet-electric/30 text-violet-electric' : 'bg-white/[0.03] border-white/10 text-white/40 hover:text-white/60'}`} data-id="element-859">
                {area}
                <span className="ml-1 opacity-50" data-id="element-860">{areaCounts[area]}</span>
              </button>)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8 pb-32" data-id="element-861">
        {filteredMonths.length === 0 ? <div className="text-center py-24" data-id="element-862">
            <div className="text-4xl mb-4 opacity-20" data-id="element-863">∅</div>
            <p className="text-white/40 font-medium" data-id="element-864">
              No changes match your filters
            </p>
            <p className="text-white/20 text-sm mt-1" data-id="element-865">
              Try adjusting your search or filter criteria.
            </p>
          </div> : <div className="space-y-10" data-id="element-866">
            {filteredMonths.map(({
          month,
          items
        }) => <section key={month} data-id="element-867">
                {/* Month header */}
                <div className="flex items-baseline gap-3 mb-4 pb-3 border-b border-white/[0.06]" data-id="element-868">
                  <h2 className="text-lg font-semibold text-white tracking-tight" data-id="element-869">
                    {month}
                  </h2>
                  <span className="text-xs font-mono text-white/25" data-id="element-870">
                    {items.length} highlight{items.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Bullet items — grouped by tag type */}
                <div className="space-y-5" data-id="element-871">
                  {ALL_TAGS.filter(tag => items.some(item => item.tag === tag)).map(tag => {
              const tagItems = items.filter(item => item.tag === tag);
              if (tagItems.length === 0) return null;
              return <div key={tag} data-id="element-872">
                        {/* Tag group header */}
                        <div className="flex items-center gap-3 mb-2" data-id="element-873">
                          <TagBadge tag={tag} data-id="element-874" />
                          <div className="flex-1 h-px bg-white/[0.05]" data-id="element-875" />
                        </div>
                        <div className="space-y-0.5" data-id="element-876">
                          {tagItems.map((item, i) => <div key={i} className="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.02] transition-colors" data-id="element-877">
                              <span className={`text-sm flex-shrink-0 pt-0.5 select-none leading-none ${TAG_DOT_COLORS[item.tag]}`} data-id="element-878">
                                •
                              </span>
                              <span className="text-white/70 text-sm leading-relaxed flex-1" data-id="element-879">
                                {item.text}
                              </span>
                              <span className="flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-mono bg-white/[0.03] border border-white/[0.07] text-white/30 whitespace-nowrap hidden sm:inline-flex mt-0.5" data-id="element-880">
                                {item.area}
                              </span>
                            </div>)}
                        </div>
                      </div>;
            })}
                </div>
              </section>)}
          </div>}

        {/* Footer note */}
        <div className="mt-16 pt-8 border-t border-white/[0.06]" data-id="element-881">
          <p className="text-white/25 text-sm" data-id="element-882">
            Questions about a specific release?{' '}
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="text-violet-electric/60 hover:text-violet-electric transition-colors underline underline-offset-2" data-id="element-883">
              Talk to our team.
            </a>
          </p>
        </div>
      </div>
    </div>;
}